import { defineComponent, PropType, ref } from "vue";

import ImageFallback from "../../../components/imageFallback.jsx";
import {
    getFormattedTime,
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
} from "../../../utils.js";
import type { TimeRangeParams } from "../timeRange.js";
import { VoiceSessionDetailsModal } from "./VoiceSessionDetailsModal.js";
import type { VoiceSessionListItem } from "./sessionModel.js";

export const VoiceSessionsCard = defineComponent({
    name: "VoiceSessionsCard",

    props: {
        title: {
            type: String,
            required: true,
        },
        emptyText: {
            type: String,
            required: true,
        },
        sessions: {
            type: Array as PropType<VoiceSessionListItem[]>,
            required: true,
        },
        currentUserId: {
            type: String,
            required: true,
        },
        timeRange: {
            type: Object as PropType<TimeRangeParams>,
            required: true,
        },
    },

    setup(props) {
        const selectedSessionId = ref<string | null>(null);

        function closeDetails() {
            selectedSessionId.value = null;
        }

        function openDetails(sessionId: string) {
            selectedSessionId.value = sessionId;
        }

        function renderMainParticipant(session: VoiceSessionListItem) {
            const participant = session.participants.find((item) => !item.primary)
                ?? session.participants[0];

            if (!participant)
                return null;

            const profilePath = `/users/${participant.userId}`;

            return (
                <>
                    <router-link to={profilePath} class="voice-interactions-avatar-link">
                        <ImageFallback
                            class="voice-interactions-profil-avatar"
                            src={participant.member ? getMemberAvatar(participant.member) : undefined}
                            default="https://cdn.discordapp.com/embed/avatars/0.png"
                            alt={participant.member ? getMemberDisplayName(participant.member) : "Utilisateur"}
                        />
                    </router-link>

                    <router-link to={profilePath} class="voice-interactions-user">
                        <div class="voice-interactions-globalname">
                            {participant.member ? getMemberDisplayName(participant.member) : session.title}
                        </div>

                        <div class="voice-interactions-username">
                            {participant.member ? getMemberTag(participant.member) : session.subtitle}
                        </div>
                    </router-link>
                </>
            );
        }

        return () => (
            <>
                <div class="voice-sessions-card">
                    <header class="voice-sessions-card-header">
                        <div>
                            <h2>{props.title}</h2>
                            <p>{props.sessions.length} session{props.sessions.length > 1 ? "s" : ""} sur la période.</p>
                        </div>
                    </header>

                    {props.sessions.length ? (
                        <div class="voice-interactions-list">
                            {props.sessions.map((session, index) => (
                                <div class="voice-interactions-list-entry" key={session.id}>
                                    <div class="voice-interactions-number">
                                        {index + 1}.
                                    </div>

                                    {renderMainParticipant(session)}

                                    <div class="voice-interactions-total-time">
                                        {getFormattedTime(session.duration)}
                                    </div>

                                    <div class="voice-interactions-last-interaction-time">
                                        <b>{new Date(session.startedAt).toLocaleTimeString("fr-FR")}</b>
                                        <span>{new Date(session.startedAt).toLocaleDateString("fr-FR")}</span>
                                    </div>

                                    <button
                                        type="button"
                                        class="voice-session-icon-button"
                                        title="Voir les détails"
                                        onClick={() => openDetails(session.id)}
                                    >
                                        <span class="material-symbols-outlined">timeline</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div class="voice-sessions-empty">
                            {props.emptyText}
                        </div>
                    )}
                </div>

                {selectedSessionId.value && (
                    <VoiceSessionDetailsModal
                        sessions={props.sessions}
                        selectedSessionId={selectedSessionId.value}
                        currentUserId={props.currentUserId}
                        timeRange={props.timeRange}
                        onSelectSession={(sessionId) => {
                            selectedSessionId.value = sessionId;
                        }}
                        onClose={closeDetails}
                    />
                )}
            </>
        );
    },
});
